import React, { Component } from 'react';
import { Toast } from 'antd-mobile';

export default class VideoUploader extends Component {
  state = {
    videoFiles: [],
    videoUploadProgress: {},
    showReupload: false
  };
  
  componentDidMount() {
    this.initVideoFromProps();
  }
  
  componentDidUpdate(prevProps) {
    // 当传入的videoUrl发生变化时，更新组件状态
    if (prevProps.videoUrl !== this.props.videoUrl && this.props.videoUrl) {
      this.initVideoFromProps();
    }
  }
  
  // 从props初始化视频数据
  initVideoFromProps = () => {
    const { videoFiles, disabled } = this.props;
    
    if (videoFiles && videoFiles.length > 0 && this.state.videoFiles.length === 0) {
      const newVideoFiles = videoFiles.map((video, index) => ({
        id: Date.now() + index,
        url: video.videoUrl || video.url,
        videoUrl: video.videoUrl || video.url,
        name: video.videoTitle || video.name || `视频${index + 1}`,
        isInitial: true, // 标记为初始视频
        disabled: disabled // 保存disabled状态
      }));
      
      this.setState({ videoFiles: newVideoFiles });
    }
  };

  // 处理视频上传
  handleVideoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // 检查文件类型
    const validVideoTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
    if (!validVideoTypes.includes(file.type)) {
      Toast.fail("请上传有效的视频文件(mp4, mov, avi, webm)", 2);
      event.target.value = '';
      return;
    }

    // 检查文件大小，限制为50MB
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      Toast.fail(`视频文件大小不能超过50MB，当前大小: ${(file.size / (1024 * 1024)).toFixed(2)}MB`, 2);
      event.target.value = '';
      return;
    }
    
    // 检查是否达到最大视频数量限制
    const { maxCount = 1 } = this.props;
    if (this.state.videoFiles.length >= maxCount) {
      Toast.fail(`最多只能上传${maxCount}个视频`, 2);
      event.target.value = '';
      return;
    }

    // 显示加载提示
    Toast.loading("视频处理中...", 0);

    // 创建视频预览URL
    const videoUrl = URL.createObjectURL(file);
    const videoId = Date.now();

    // 添加到视频文件列表（显示预览）
    const newVideoFiles = [...this.state.videoFiles];
    newVideoFiles.push({
      id: videoId,
      file,
      url: videoUrl,
      name: file.name,
      size: file.size
    });

    this.setState({ videoFiles: newVideoFiles });

    // 创建FormData对象
    const formData = new FormData();
    formData.append('file', file);

    // 模拟上传进度
    const progressInterval = setInterval(() => {
      this.setState(prevState => {
        const currentProgress = (prevState.videoUploadProgress[videoId]?.progress || 0);
        const newProgress = Math.min(95, currentProgress + Math.random() * 10);
        
        return {
          videoUploadProgress: {
            ...prevState.videoUploadProgress,
            [videoId]: {
              status: 'uploading',
              progress: newProgress
            }
          }
        };
      });
    }, 500);

    try {
      // 使用uploadFile函数上传视频文件
      const result = await this.props.onUpload(formData);
      
      // 清除进度模拟定时器
      clearInterval(progressInterval);
      
      if (result.code === 200 && result.data && result.data.url) {
        // 更新上传进度为完成
        const updatedVideoFiles = [...this.state.videoFiles].map(v => 
          v.id === videoId ? { 
            ...v, 
            file: null, 
            url: result.data.url, 
            videoUrl: result.data.url,
            name: file.name || '视频文件'
          } : v
        );
        
        this.setState({
          videoFiles: updatedVideoFiles,
          videoUploadProgress: {
            ...this.state.videoUploadProgress,
            [videoId]: {
              status: 'success',
              progress: 100
            }
          }
        }, () => {
          // 在状态更新后调用onChange回调
          if (this.props.onChange) {
            // 传递视频URL和其他必要信息给父组件
            this.props.onChange({
              videoUrl: result.data.url,
              videoName: file.name,
              videoFiles: updatedVideoFiles
            });
          }
        });
        
        Toast.success(`视频上传成功`, 1);
      } else {
        throw new Error(result.message || '上传失败');
      }
    } catch (error) {
      // 清除进度模拟定时器
      clearInterval(progressInterval);
      
      // 更新上传状态为错误
      this.setState(prevState => ({
        videoUploadProgress: {
          ...prevState.videoUploadProgress,
          [videoId]: {
            status: 'error',
            progress: 0
          }
        }
      }));
      
      Toast.fail(`视频上传失败: ${error.message}`, 2);
    }
    
    // 关闭加载提示
    Toast.hide();
    // 清空input，以便可以重复选择同一个文件
    event.target.value = '';
  };

  // 删除视频文件
  removeVideo = (id) => {
    const newVideoFiles = this.state.videoFiles.filter(video => video.id !== id);
    this.setState({
      videoFiles: newVideoFiles
    }, () => {
      // 在状态更新后调用onChange回调
      if (this.props.onChange) {
        // 如果没有视频文件，则传递空值
        if (newVideoFiles.length === 0) {
          this.props.onChange({
            videoUrl: '',
            videoName: '',
            videoFiles: []
          });
        } else {
          // 否则传递第一个视频文件的信息
          const video = newVideoFiles[0];
          this.props.onChange({
            videoUrl: video.videoUrl || video.url,
            videoName: video.name,
            videoFiles: newVideoFiles
          });
        }
      }
    });
  };

  render() {
    const { videoFiles, videoUploadProgress } = this.state;
    const { disabled = false, maxCount = 1 } = this.props;
    const hasVideo = videoFiles.length > 0;
    const canAddMore = videoFiles.length < maxCount;
    // 检查是否为回显状态（有视频且是初始视频）
    const hasInitialVideo = hasVideo && videoFiles.some(v => v.isInitial);

    return (
      <div className="videoUploadContainer">
        {!disabled && canAddMore && (
          <div className="videoUploadBtn">
            <input 
              type="file" 
              accept="video/mp4,video/quicktime,video/x-msvideo,video/webm" 
              onChange={this.handleVideoUpload} 
              style={{ display: 'none' }} 
              id="videoUploadInput" 
            />
            <label htmlFor="videoUploadInput" className="uploadLabel">
              {videoFiles.length > 0 ? '添加视频' : '选择视频'}
            </label>
            <p className="uploadTip">支持mp4、mov等格式，单个视频不超过50MB，时长不超过3分钟{maxCount > 1 ? `，最多上传${maxCount}个视频` : ''}</p>
          </div>
        )}
        
        {videoFiles.length > 0 && (
          <div className="videoList">
            {videoFiles.map(video => (
              <div key={video.id} className="videoItem">
                <video 
                  src={video.url} 
                  controls 
                  className="videoPreview"
                  width="50%"
                  height="auto"
                  poster=""
                  preload="metadata"
                />
                <div className="videoInfo">
                  <div className="videoNameContainer">
                    <span className="videoName">{video.name}</span>
                  </div>
                  {video.size && (
                    <span className="videoSize">
                      {(video.size / (1024 * 1024)).toFixed(2)}MB
                    </span>
                  )}
                  {!disabled && (
                    <span 
                      className="videoDelete" 
                      onClick={() => this.removeVideo(video.id)}
                    >
                      删除
                    </span>
                  )}
                </div>
                
                {videoUploadProgress[video.id] && (
                  <div className="videoUploadProgress">
                    <div 
                      className={`progressBar ${videoUploadProgress[video.id].status}`}
                      style={{ width: `${videoUploadProgress[video.id].progress}%` }}
                    ></div>
                    <div className="progressStatus">
                      {videoUploadProgress[video.id].status === 'uploading' && (
                        <span>上传中 {Math.floor(videoUploadProgress[video.id].progress)}%</span>
                      )}
                      {videoUploadProgress[video.id].status === 'success' && (
                        <span className="success">上传成功</span>
                      )}
                      {videoUploadProgress[video.id].status === 'error' && (
                        <span className="error">上传失败</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}